import { DatePicker } from "@devhop/ui";
import { format } from "date-fns";
import {
  combineDateAndQuarterTime,
  DEFAULT_QUARTER_TIME,
  parseLocalDateOnly,
  splitDateTimeLocalValue,
} from "@/shared/lib/datetime-local-form";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";

export type DateQuarterTimeFieldProps = {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  label?: React.ReactNode;
  disabled?: boolean;
  allowEmpty?: boolean;
  defaultTime?: string;
  className?: string;
  hint?: React.ReactNode;
  error?: string;
};

/**
 * Studio-style: `DatePicker` + `QuarterHourMinuteSelect`.
 * Value: `"yyyy-MM-ddTHH:mm"` or empty / null when allowEmpty.
 */
export function DateQuarterTimeField({
  value,
  onChange,
  label,
  disabled,
  allowEmpty = true,
  defaultTime = DEFAULT_QUARTER_TIME,
  className,
  hint,
  error,
}: DateQuarterTimeFieldProps) {
  const { date, time } = splitDateTimeLocalValue(value);
  const timeValue = time || (date ? defaultTime : "");
  const timeDisabled = disabled || (!date && allowEmpty);
  const pickerDate = date ? parseLocalDateOnly(date) : null;

  const commit = (nextDate: string, nextTime: string) => {
    if (!nextDate) {
      onChange(allowEmpty ? null : "");
      return;
    }
    onChange(combineDateAndQuarterTime(nextDate, nextTime || defaultTime));
  };

  return (
    <div className={className}>
      {label != null ? (
        <div className="mb-2 font-medium text-sm leading-none">{label}</div>
      ) : null}

      <div className="space-y-3">
        <div>
          <p className="mb-1.5 text-muted-foreground text-xs">ວັນທີ</p>
          <DatePicker
            value={pickerDate ?? undefined}
            disabled={disabled}
            className="w-full"
            onChange={(next) => {
              const picked =
                next instanceof Date
                  ? next
                  : Array.isArray(next)
                    ? next[0]
                    : (next as { from?: Date } | undefined)?.from;
              if (!picked) {
                commit("", "");
                return;
              }
              const nextDate = format(picked, "yyyy-MM-dd");
              commit(nextDate, timeValue || defaultTime);
            }}
          />
        </div>

        <div>
          <p className="mb-1.5 text-muted-foreground text-xs">ເວລາ</p>
          <QuarterHourMinuteSelect
            value={timeValue}
            disabled={timeDisabled}
            className="w-full"
            onChange={(nextTime) => {
              if (!date) {
                if (allowEmpty) onChange(null);
                return;
              }
              commit(date, nextTime);
            }}
          />
        </div>
      </div>

      {hint ? (
        <p className="mt-1.5 text-muted-foreground text-xs">{hint}</p>
      ) : null}
      {error ? <p className="mt-1 text-red-500 text-xs">{error}</p> : null}
    </div>
  );
}
